<template>
  <div class="app-header" role="banner">
    <b-modal
      id="modalTermsOfUse"
      ref="modalTermsOfUse"
      size="xl"
      header-class="terms-of-use-modal-header"
      :aria-label="$t('termosdeuso')"
      :no-close-on-esc="true"
      :no-close-on-backdrop="true"
      :hide-header-close="true"
      @ok="modalOk"
      @hide="modalOk"
      @cancel="modalOk"
    >
      <template #modal-header>
        <div class="terms-of-use-header">
          <h5 class="terms-of-use-header__title">
            {{ $t('termosdeuso') }}
          </h5>
          <p class="terms-of-use-header__subtitle">
            {{ $t('termosdeusosubtitulo') }}
          </p>
        </div>
      </template>

      <div class="terms-of-use-body">
        <terms-of-use-pdf-viewer :file="termodeusoFile" />
      </div>

      <template #modal-footer="{ ok }">
        <div class="terms-of-use-footer">
          <b-form-checkbox
            id="checkbox-1"
            v-model="termsOfUse"
            name="checkbox-1"
            :value="true"
            :unchecked-value="false"
            class="terms-of-use-footer__checkbox"
          >
            {{ $t('aceitotermosdeuso') }}
          </b-form-checkbox>
          <b-button
            variant="none"
            class="terms-of-use-footer__button"
            :disabled="!termsOfUse"
            @click="ok()"
          >
            {{ $t('avancar') }}
          </b-button>
        </div>
      </template>
    </b-modal>

    <hamburger
      id="hamburger-container"
      :is-active="sidebar.opened"
      :aria-label="$t('togglesidebar')"
      @toggleClick="toggleSideBar"
    />

    <breadcrumb v-if="device !== 'mobile'" id="breadcrumb-container" />

    <div class="app-header__spacer" />

    <company-switcher />

    <template v-if="user && device !== 'mobile'">
      <span aria-hidden="true" class="header-user-menu-divider" />
      <header-user-menu />
    </template>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import Breadcrumb from '@/components/Breadcrumb'
import Hamburger from '@/components/Hamburger'
import TermsOfUsePdfViewer from '@/components/TermsOfUsePdfViewer'
import HeaderUserMenu from './UserMenu/HeaderUserMenu'
import CompanySwitcher from './CompanySwitcher'
import i18n from '@/plugins/i18n'
import { getInfo } from '@/api/user'
import { centralDemarcoUserService } from '@/services/central-demarco-user-service'
import { hideLoader, showLoader } from '@/utils/loading'
import router from '../../router'
import termodeusoFile from '@/assets/pdf/termodeuso.pdf'

export default {
  components: {
    Breadcrumb,
    Hamburger,
    TermsOfUsePdfViewer,
    HeaderUserMenu,
    CompanySwitcher
  },
  data() {
    return {
      languages: [
        { flag: 'pt-BR', language: 'pt-BR', title: 'Português' },
        { flag: 'us', language: 'en', title: 'English' },
        { flag: 'es', language: 'es', title: 'Español' }
      ],
      user: null,
      termsOfUse: false,
      termodeusoFile
    }
  },
  computed: {
    ...mapGetters(['sidebar', 'avatar', 'device', 'userId', 'name']),
    ...mapState({
      userTermsOfUse: (state) => state.user.userTermsOfUse
    })
  },
  created() {
    this.getLocale()
    getInfo()
      .then(({ data }) => {
        this.user = data
      })
      .catch(() => {
        router.push('/error')
      })
  },
  mounted() {
    this.verifyTermsOfUse()
  },
  methods: {
    getLocale() {
      let lastLocale =
        localStorage.getItem('lastLocale') ||
        navigator.userLanguage ||
        (navigator.languages &&
          navigator.languages.length &&
          navigator.languages[0]) ||
        navigator.language ||
        navigator.browserLanguage ||
        navigator.systemLanguage ||
        'pt-BR'
      if (lastLocale !== 'en' && lastLocale !== 'es') {
        lastLocale = 'pt-BR'
      }
      this.$store.state.user.locale = lastLocale
      i18n.locale = lastLocale
    },

    toggleSideBar() {
      this.$store.dispatch('app/toggleSideBar')
    },

    async verifyTermsOfUse() {
      if (!this.userTermsOfUse) this.$refs.modalTermsOfUse.show()
    },

    async modalOk() {
      if (!this.termsOfUse) router.push('/terms-not-accepted')
      else {
        try {
          showLoader()
          const { data: user } =
            await centralDemarcoUserService.getByReferenceId(this.userId)
          user.termsOfUse = this.termsOfUse
          await centralDemarcoUserService.save(user)
          this.$store.dispatch('user/updateTermsOfUse', true)
        } catch (error) {
          router.push('/error')
        } finally {
          hideLoader()
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.terms-of-use-header {
  width: 100%;
  text-align: center;
  font-family: 'Poppins', sans-serif;
  color: #495057;

  &__title {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: inherit;
  }

  &__subtitle {
    margin: 4px 0 0;
    font-size: 14px;
    color: #757575;
  }
}

// index.scss torna .modal-body um flex container global; sem largura explicita
// o corpo encolhe para o tamanho do conteudo (toolbar), e o viewer calcula a
// escala a partir desse container estreito.
.terms-of-use-body {
  width: 100%;
  text-align: left;
}

.terms-of-use-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  font-family: 'Poppins', sans-serif;

  @media (max-width: 575.98px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;

    .terms-of-use-footer__button {
      width: 100%;
    }
  }

  &__checkbox {
    color: #374151;

    ::v-deep .custom-control-label {
      font-family: inherit;
      font-weight: 500;
      font-size: 14px;
      color: inherit;
      // Espacamento extra entre o checkbox e o texto (o ::before/::after
      // continuam ancorados na borda do label via left:-1.5rem, entao esse
      // padding so afasta o texto, sem mover a caixinha do checkbox).
      padding-left: 8px;
    }

    // Dimensões do checkbox e alinhamento vertical com o texto (14px/
    // line-height 21px): centralizado via top = (21 - 18) / 2 = 1.5px.
    ::v-deep .custom-control-label::before,
    ::v-deep .custom-control-label::after {
      width: 18px;
      height: 18px;
      top: 1.5px;
    }
  }

  &__button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-family: inherit;
    font-weight: 500;
    background-color: #53933a;
    border-color: #53933a;
    color: #fff;

    &:hover:not(:disabled) {
      background-color: #467a30;
      border-color: #467a30;
      color: #fff;
    }

    &:disabled {
      background-color: #53933a;
      border-color: #53933a;
      opacity: 0.5;
      cursor: not-allowed;
    }

    // Impede que o :hover do Bootstrap (.btn:hover) escureça o texto quando
    // desabilitado - nada deve mudar visualmente alem do cursor.
    &:hover:disabled {
      background-color: #53933a;
      border-color: #53933a;
      color: #fff;
      cursor: not-allowed;
    }
  }
}
</style>

<style lang="scss">
// Sem scoped: o b-modal renderiza o header fora da arvore deste componente
// (mesmo padrao de header-class usado em TrainingPlanning.vue).
.terms-of-use-modal-header {
  border-bottom: 0 !important;
}
</style>
